import {
  listOutgoingEmailLogItems,
  retrieveOutgoingEmailLogItem,
} from '@app/gen/logs';
import {OutgoingEmailLogItem} from '@app/gen/schemas/outgoing-email-log-item';
import {apiClient, queryClient} from '@common/http/query-client';
import {mutationOptions, queryOptions} from '@tanstack/react-query';
import {FirstParam} from '@ui/utils/ts/extract-params';

export const baseOutgoingEmailKey = ['outgoing-email-log'];

export const EMAIL_LOG_HIGHLIGHT_EVENT = 'cd-email-log-highlight';
export const EMAIL_LOG_PREVIEW_EVENT = 'cd-email-log-preview';

export function highlightResentEmail(id: number) {
  window.dispatchEvent(
    new CustomEvent(EMAIL_LOG_HIGHLIGHT_EVENT, {detail: id}),
  );
}

export function previewEmailLogItem(id: number) {
  window.dispatchEvent(new CustomEvent(EMAIL_LOG_PREVIEW_EVENT, {detail: id}));
}

export type ResendEmailResponse = {
  data?: OutgoingEmailLogItem;
  resent?: OutgoingEmailLogItem | null;
};

export type ResendManyEmailResponse = {
  status: string;
  resent: number;
  failed: number[];
  highlight?: number | null;
};

export const listOutgoingEmailLogItemsOptions = (
  params?: FirstParam<typeof listOutgoingEmailLogItems>,
) => {
  return queryOptions({
    queryKey: [...baseOutgoingEmailKey, params],
    queryFn: () => listOutgoingEmailLogItems(params),
  });
};

export const retrieveOutgoingEmailLogItemOptions = (id: number) => {
  return queryOptions({
    queryKey: [...baseOutgoingEmailKey, `${id}`],
    queryFn: () => retrieveOutgoingEmailLogItem(id),
  });
};

export const resendOutgoingEmailLogItemOptions = (id: number) => {
  return mutationOptions({
    mutationFn: async () => {
      const {data} = await apiClient.post<ResendEmailResponse>(
        `logs/outgoing-email/${id}/resend`,
      );
      return data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: baseOutgoingEmailKey});
    },
  });
};

export const exportOutgoingEmailLogCsvOptions = mutationOptions({
  mutationFn: async (payload?: {
    query?: string;
    status?: string;
    created_at?: string;
    ids?: string;
  }) => {
    const {data} = await apiClient.post<{
      result: string;
      downloadPath: string | null;
    }>('logs/outgoing-email/csv-export', payload);
    return data;
  },
});

export const logRetentionPreviewOptions = (params: {
  email_log_keep?: string;
  schedule_log_keep?: string;
  error_log_keep?: string;
}) =>
  queryOptions({
    queryKey: ['log-retention-preview', params],
    queryFn: async () => {
      const {data} = await apiClient.get<{
        email: number;
        schedule: number;
        error_files: number;
        email_keep: string;
        schedule_keep: string;
        error_keep: string;
      }>('logs/retention-preview', {params});
      return data;
    },
  });

export const resendOutgoingEmailLogItemsOptions = mutationOptions({
  mutationFn: async (ids: number[]) => {
    const {data} = await apiClient.post<ResendManyEmailResponse>(
      'logs/outgoing-email/resend',
      {ids},
    );
    return data;
  },
  onSuccess: () => {
    queryClient.invalidateQueries({queryKey: baseOutgoingEmailKey});
  },
});

export const deleteOutgoingEmailLogItemOptions = (id: number) => {
  return mutationOptions({
    mutationFn: async () => {
      const {data} = await apiClient.delete(`logs/outgoing-email/${id}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: baseOutgoingEmailKey});
    },
  });
};

export const deleteOutgoingEmailLogItemsOptions = mutationOptions({
  mutationFn: async (ids: number[]) => {
    const {data} = await apiClient.delete('logs/outgoing-email', {
      data: {ids},
    });
    return data as {status: string; deleted: number};
  },
  onSuccess: () => {
    queryClient.invalidateQueries({queryKey: baseOutgoingEmailKey});
  },
});
